import { atom } from 'jotai'

import { COOKIE_KEYS } from '@/lib/configs/auth-config'
import { atomWithCookie } from '@/lib/utils/atom-util'
import { AuthState, initialAuthState } from '@/types/auth'

const toPersistedAuthState = (authState: AuthState): AuthState => ({
  ...authState,
  refreshToken: null,
})

export const authAtom = atomWithCookie<AuthState>(COOKIE_KEYS.AUTH_STATE, initialAuthState, {
  toCookieValue: toPersistedAuthState,
})

// Helper functions for updating authAtom
export const updateAuthTokens = (
  authState: AuthState,
  accessToken: string | null,
  refreshToken: string | null
) => ({
  ...authState,
  accessToken,
  refreshToken,
  // A successful refresh re-establishes the session, but only claim full
  // authentication when a real user profile is present (mirrors the
  // middleware's requireUser rule) — never promote a profile-less state.
  isAuthenticated: authState.isAuthenticated || Boolean(accessToken && authState.user),
})

// Drops only the credentials, on a rejected/failed refresh. `user` is
// intentionally preserved: the profile in the AUTH_STATE cookie is what lets
// the middleware's requireUser refresh path re-authenticate the session from
// the httpOnly refresh-token cookie — nulling it here used to strand users on
// /login forever with a perfectly valid refresh token still rotating.
// For an explicit user-initiated logout use clearAuthState instead.
export const clearAuthTokens = (authState: AuthState) => ({
  ...authState,
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
})

// Full reset for an explicit logout — unlike clearAuthTokens, this also drops
// the user profile so nothing about the outgoing user lingers in the cookie.
export const clearAuthState = (): AuthState => ({ ...initialAuthState })

export const emailResendAtom = atom<{
  email: string
  cooldown: number
  password?: string
}>({
  email: '',
  cooldown: 0,
  password: undefined,
})

/**
 * Auth Dialog 전역 상태.
 * - open: Dialog 표시 여부
 * - pendingAction: 로그인 성공 후 자동 실행할 콜백 (선택)
 *
 * 사용 예시:
 *   const openAuthDialog = useSetAtom(authDialogAtom)
 *   openAuthDialog({ open: true, pendingAction: () => handleOrder() })
 */
export const authDialogAtom = atom<{
  open: boolean
  pendingAction?: () => void
}>({
  open: false,
  pendingAction: undefined,
})

// Re-export storage atom for backward compatibility
export { resetPasswordEmailAtom } from '@/state/storage'
